/**
 * CastBenchPanel — modernized cast-bench section of the AbilityTestRig.
 *
 * Visual ability browsing with genuine icons, badges, mana/cooldown chips,
 * detailed ability facts inspection card, and one-click casting.
 */
import React, { useMemo, useState } from 'react';
import {
  COLORS,
  SPACE,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
} from '../../theme';
import { Section } from './shared';
import { rigStyles as styles } from './abilityRigStyles';
import type { AbilityMetadata } from '../../../systems/player/abilities/AbilityRegistry';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { hasAbilityIcon, abilityIconPath } from '../../../rendering/abilities/AbilityIconRegistry';
import { ItemSlot } from '../../components/ItemKit';
import { Button, Badge } from '../../primitives';

export type LastCastResult = 'success' | 'pending' | null;

export interface CastBenchPanelProps {
  filter: string;
  onFilterChange: (next: string) => void;
  abilities: AbilityMetadata[];
  lastCastId: string | null;
  lastCastResult: LastCastResult;
  onCast: (abilityId: string) => void;
}

const CAST_LIST_LIMIT = 50;

export const CastBenchPanel: React.FC<CastBenchPanelProps> = ({
  filter,
  onFilterChange,
  abilities,
  lastCastId,
  lastCastResult,
  onCast,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(lastCastId || abilities[0]?.id || null);

  const activeId = selectedId || lastCastId || abilities[0]?.id || null;

  const selectedAbility = useMemo(() => {
    if (!activeId) return null;
    return abilities.find((a) => a.id === activeId) ?? null;
  }, [abilities, activeId]);

  const selectedData = useMemo(() => {
    if (!activeId) return null;
    return getAbilityData(activeId);
  }, [activeId]);

  const handleSelectAndCast = (id: string) => {
    setSelectedId(id);
    onCast(id);
  };

  return (
    <Section title="Cast bench">
      <div style={styles.searchBar}>
        <span style={styles.searchIcon}>🔍</span>
        <input
          type="text"
          placeholder="Filter abilities by name or ID…"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          style={styles.searchInput}
        />
        {filter && (
          <button
            type="button"
            onClick={() => onFilterChange('')}
            style={styles.clearSearchBtn}
          >
            ✕
          </button>
        )}
      </div>

      {lastCastId && (
        <div style={styles.lastCastBar}>
          <div>
            Last cast: <strong style={{ color: COLORS.textPrimary }}>{lastCastId}</strong>
          </div>
          <Badge
            tone={lastCastResult === 'pending' ? 'info' : 'success'}
            size="sm"
          >
            {lastCastResult === 'pending' ? '◐ PENDING' : '✓ FIRED'}
          </Badge>
        </div>
      )}

      <div style={styles.abilityList}>
        {abilities.slice(0, CAST_LIST_LIMIT).map((a) => {
          const isSelected = a.id === activeId;
          const isLast = a.id === lastCastId;
          const iconUrl = hasAbilityIcon(a.id) ? abilityIconPath(a.id) : null;
          const cdSeconds = a.cooldown ? (a.cooldown / 1000).toFixed(1) : null;

          return (
            <button
              key={a.id}
              type="button"
              style={{
                ...styles.abilityRow,
                ...(isSelected ? styles.abilityRowActive : {}),
              }}
              onClick={() => handleSelectAndCast(a.id)}
              title={`${a.name || a.id} (${a.id}) — click to cast`}
            >
              <ItemSlot
                item={{
                  id: a.id,
                  name: a.name || a.id,
                  icon: iconUrl || undefined,
                }}
                size="xs"
              />
              <div style={styles.abilityInfo}>
                <div style={styles.abilityName}>{a.name || a.id}</div>
                <div style={styles.abilityId}>{a.id}</div>
              </div>
              <div style={styles.abilityMeta}>
                {a.target && (
                  <Badge
                    tone={a.target === 'self' || a.target === 'friendly' ? 'success' : 'danger'}
                    size="sm"
                  >
                    {a.target}
                  </Badge>
                )}
                {a.manaCost ? (
                  <span style={styles.costChip}>{a.manaCost} MP</span>
                ) : null}
                {cdSeconds ? (
                  <span style={styles.cdChip}>{cdSeconds}s</span>
                ) : null}
                {isLast && (
                  <span style={{ color: PLAYER_UI.brassBright, fontSize: 10 }}>●</span>
                )}
              </div>
            </button>
          );
        })}
        {abilities.length > CAST_LIST_LIMIT && (
          <div style={{ color: COLORS.textMuted, fontSize: FONT_SIZE.xs, padding: SPACE.xs, textAlign: 'center' }}>
            +{abilities.length - CAST_LIST_LIMIT} more abilities — refine filter to view
          </div>
        )}
      </div>

      {selectedAbility && (
        <div style={styles.inspectorCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.bold, color: COLORS.textPrimary }}>
                {selectedAbility.name || selectedAbility.id}
              </div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: 'monospace' }}>
                {selectedAbility.id}
              </div>
            </div>
            {selectedAbility.target && (
              <Badge tone={selectedAbility.target === 'self' || selectedAbility.target === 'friendly' ? 'success' : 'danger'}>
                {selectedAbility.target}
              </Badge>
            )}
          </div>

          <div style={styles.factsGrid}>
            <div style={styles.factTile}>
              <div style={styles.factLabel}>MANA</div>
              <div style={styles.factVal}>
                {selectedAbility.manaCost ? `${selectedAbility.manaCost} MP` : 'Free'}
              </div>
            </div>
            <div style={styles.factTile}>
              <div style={styles.factLabel}>COOLDOWN</div>
              <div style={styles.factVal}>
                {selectedAbility.cooldown ? `${(selectedAbility.cooldown / 1000).toFixed(1)}s` : '0s'}
              </div>
            </div>
            <div style={styles.factTile}>
              <div style={styles.factLabel}>RANGE</div>
              <div style={styles.factVal}>
                {selectedData?.range ? `${selectedData.range}px` : selectedAbility.range ? `${selectedAbility.range}px` : 'Melee'}
              </div>
            </div>
            <div style={styles.factTile}>
              <div style={styles.factLabel}>SCHOOL</div>
              <div style={styles.factVal}>
                {selectedData?.school || 'physical'}
              </div>
            </div>
          </div>

          <Button
            variant="brass"
            size="md"
            fullWidth
            onClick={() => onCast(selectedAbility.id)}
          >
            ⚡ Cast {selectedAbility.name || selectedAbility.id}
          </Button>
        </div>
      )}
    </Section>
  );
};
